import type { BoardSummary } from '@kanban/shared';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrashZone } from '../components/dnd/TrashZone.js';
import { useBoardListDnd } from '../components/dnd/useBoardListDnd.js';
import {
  useArchiveBoard,
  useBoards,
  useCreateBoard,
  useReorderBoards,
  useUsers,
} from '../lib/boards.js';
import { useCurrentUser } from '../lib/session.js';

/** Ein Board als Ziehpunkt — dieselbe Zieh-Flaeche traegt auch den Link. */
function SortableBoardLink({ board, isDragging }: { board: BoardSummary; isDragging: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: board.id });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        // Nicht `none`: vor dem Long-Press muss die Seite noch scrollen
        // koennen, danach uebernimmt dnd-kit das Scrollen selbst.
        touchAction: 'manipulation',
        cursor: 'grab',
      }}
    >
      <Link
        to={`/board/${board.id}`}
        data-testid="board-link"
        className="flex items-center justify-between rounded-[var(--radius)] border border-border bg-surface px-4 py-3 shadow-[var(--shadow-1)]"
        style={{ minHeight: 'var(--touch-target)' }}
      >
        <span className="font-medium">
          {board.icon ? `${board.icon} ` : ''}
          {board.title}
        </span>
        <span className="text-xs text-muted">{board.cardCount} Karten</span>
      </Link>
    </div>
  );
}

/** Startseite: Auswahl unter mehreren Boards (Haushalt, Garten, Urlaub …). */
export function BoardListPage() {
  const user = useCurrentUser();
  const boards = useBoards();
  const users = useUsers();
  const createBoard = useCreateBoard();
  const reorderBoards = useReorderBoards();
  const archiveBoard = useArchiveBoard();
  const [title, setTitle] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const boardIds = (boards.data ?? []).map((board) => board.id);

  const dnd = useBoardListDnd({
    boardIds,
    onReorder: (orderedIds) => reorderBoards.mutate(orderedIds),
    onTrash: (boardId) => {
      const board = boards.data?.find((entry) => entry.id === boardId);
      if (!board) return;
      // Toast und Rueckgaengig stecken bereits in `useArchiveBoard` — hier
      // nur die Bezugsdaten des konkreten Boards mitgeben.
      archiveBoard.mutate({ boardId, updatedAt: board.updatedAt });
    },
  });

  return (
    <div className="h-full overflow-y-auto px-4 py-4">
      <h1 className="text-lg font-semibold">Boards</h1>

      {boards.isLoading && <p className="mt-4 text-sm text-muted">Lädt …</p>}
      {boards.isError && (
        <p role="alert" className="mt-4 text-sm text-danger">
          Boards konnten nicht geladen werden.
        </p>
      )}

      <DndContext {...dnd.dndContextProps}>
        <SortableContext items={boardIds} strategy={verticalListSortingStrategy}>
          <ul className="mt-4 space-y-2">
            {boards.data?.map((board) => (
              <li key={board.id}>
                <SortableBoardLink board={board} isDragging={dnd.activeId === board.id} />
              </li>
            ))}
          </ul>
        </SortableContext>
        <TrashZone visible={dnd.showTrash} active={dnd.overTrash} />
      </DndContext>

      {boards.data?.length === 0 && (
        <p className="mt-6 text-sm text-muted">
          Noch kein Board vorhanden.
          {user.role === 'admin' ? ' Lege unten das erste an.' : ' Der Administrator legt eines an.'}
        </p>
      )}

      {/* Boards anlegen darf nur der Admin (SPEC 2). */}
      {user.role === 'admin' &&
        (isCreating ? (
          <form
            className="mt-6 rounded-[var(--radius-lg)] border border-border bg-surface p-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!title.trim()) return;
              createBoard.mutate(
                { title: title.trim(), memberIds },
                {
                  onSuccess: () => {
                    setTitle('');
                    setMemberIds([]);
                    setIsCreating(false);
                  },
                },
              );
            }}
          >
            <label className="block text-xs font-medium text-muted" htmlFor="board-title">
              Titel
            </label>
            <input
              id="board-title"
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
              style={{ minHeight: 'var(--touch-target)' }}
            />

            <fieldset className="mt-4">
              <legend className="text-xs font-medium text-muted">Mitglieder</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {users.data
                  ?.filter((candidate) => candidate.id !== user.id)
                  .map((candidate) => {
                    const selected = memberIds.includes(candidate.id);
                    return (
                      <button
                        key={candidate.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() =>
                          setMemberIds((current) =>
                            selected
                              ? current.filter((id) => id !== candidate.id)
                              : [...current, candidate.id],
                          )
                        }
                        className={`rounded-full border px-3 text-sm ${
                          selected ? 'border-accent bg-accent-soft text-accent' : 'border-border'
                        }`}
                        style={{ minHeight: 'var(--touch-target)' }}
                      >
                        {candidate.name}
                      </button>
                    );
                  })}
              </div>
            </fieldset>

            <div className="mt-4 flex gap-2">
              <button
                type="submit"
                disabled={createBoard.isPending}
                className="flex-1 rounded-[var(--radius)] bg-accent px-4 text-sm font-medium text-[var(--color-accent-contrast)] disabled:opacity-60"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                Anlegen
              </button>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="rounded-[var(--radius)] border border-border px-4 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className="mt-6 w-full rounded-[var(--radius)] border border-dashed border-border-strong px-4 text-sm text-muted"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            + Neues Board
          </button>
        ))}
    </div>
  );
}
